import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

const MODES = [
  ['legacy_cancel', 'Legacy cancel (bridge only)'],
  ['observe_only', 'Observe only'],
  ['missing_from_api', 'Missing From API'],
];

const validateOverrides = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return {};
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Program overrides must be a JSON object.');
  }
  Object.entries(parsed).forEach(([programName, override]) => {
    if (!override || typeof override !== 'object' || Array.isArray(override)) {
      throw new Error(`${programName} override must be an object.`);
    }
    if (!MODES.some(([mode]) => mode === override.mode)) {
      throw new Error(`${programName} override must include a supported mode.`);
    }
    if (Number(override.cooldownDays) !== 7) {
      throw new Error(`${programName} override cooldownDays must be exactly 7.`);
    }
  });
  return parsed;
};

const ApiPresenceSettingsCard = ({ settings, isLoading, saveMutation, onMessage }) => {
  const [mode, setMode] = useState(settings?.mode || 'legacy_cancel');
  const [programOverrides, setProgramOverrides] = useState('');

  useEffect(() => {
    if (!settings) return;
    setMode(settings.mode || 'legacy_cancel');
    const overrides = settings.programOverrides || {};
    setProgramOverrides(Object.keys(overrides).length ? JSON.stringify(overrides, null, 2) : '');
  }, [settings]);

  const save = () => {
    let overrides;
    try {
      overrides = validateOverrides(programOverrides);
    } catch (error) {
      onMessage({ message: error.message || 'Program overrides must be valid JSON.', severity: 'error' });
      return;
    }
    saveMutation.mutate(
      { mode, cooldownDays: 7, programOverrides: overrides },
      {
        onSuccess: () => onMessage({ message: 'API presence settings updated.', severity: 'success' }),
        onError: (error) => onMessage({
          message: error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Failed to update API presence settings.',
          severity: 'error',
        }),
      }
    );
  };

  const disabled = isLoading || saveMutation.isPending;
  const availableModes = settings?.mode && settings.mode !== 'legacy_cancel'
    ? MODES.filter(([value]) => value !== 'legacy_cancel')
    : MODES;
  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title="DPE Missing From API Cooling Period"
        subheader="A validated BAC absence is held for a fixed seven-day cooling period. Mode changes are forward-only."
      />
      <CardContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Cooling and Missing From API are API-presence evidence. They do not cancel an enrollment and remain searchable and auditable without replacing enrollment lifecycle status.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel id="api-presence-mode-label">Presence mode</InputLabel>
              <Select
                labelId="api-presence-mode-label"
                label="Presence mode"
                value={mode}
                onChange={(event) => setMode(event.target.value)}
                inputProps={{ 'data-testid': 'api-presence-mode' }}
              >
                {availableModes.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Cooling period (days)" value="7" disabled />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Program Overrides (JSON)"
              value={programOverrides}
              onChange={(event) => setProgramOverrides(event.target.value)}
              placeholder='{"Website":{"mode":"observe_only","cooldownDays":7}}'
              multiline
              minRows={3}
              disabled={disabled}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={save} disabled={disabled} data-testid="api-presence-save">
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export { validateOverrides };
export default ApiPresenceSettingsCard;
